import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CreditCard, Link as LinkIcon, QrCode, Sparkles, Timer, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/seo/SEO';
import { PERSONAS } from '@/lib/homeData';
import { PROGRAMMATIC_VERTICALS } from '@/lib/programmaticData';
import { AhaToastMockup, BuyerHandoffMockup, CheckoutMockup, MobileSellerMockup } from '@/components/home/FintechMockups';

const HOW_IT_WORKS_STEPS = [
  {
    icon: Timer,
    title: 'Quote in 15 seconds',
    detail: 'Type amount once and generate a financing-ready checkout link instantly.',
    metric: '10-15s',
  },
  {
    icon: QrCode,
    title: 'Share by link, SMS, or QR',
    detail: 'Send from your phone while the buyer is still in front of you.',
    metric: '1 tap',
  },
  {
    icon: CreditCard,
    title: 'Buyer chooses monthly payments',
    detail: 'Eligible checkout can show Klarna, Affirm, Afterpay, or card.',
    metric: 'Live',
  },
  {
    icon: TrendingUp,
    title: 'Follow up when intent is hot',
    detail: 'See opens/reopens and act at the right moment.',
    metric: 'Real-time',
  },
];

const FeaturesPage: React.FC = () => {
  const appUrl = import.meta.env.VITE_APP_URL || '';
  const appSignUpUrl = appUrl ? `${appUrl}/auth?mode=signup` : '/auth?mode=signup';

  return (
    <div className="min-h-screen bg-[#f8f5ef] pb-24 text-slate-900">
      <SEO
        title="How LendLifted Works | Financing-Enabled Checkout for Small Sellers"
        description="See the full LendLifted flow: quote creation, QR/link sharing, monthly payment options at checkout, and clear follow-up timing that helps improve close rates."
        canonical="/features"
      />

      <section className="relative overflow-hidden px-6 pb-20 pt-28 md:pt-32">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -left-20 top-8 h-72 w-72 rounded-full bg-emerald-200/40 blur-3xl" />
          <div className="absolute -right-20 top-12 h-80 w-80 rounded-full bg-indigo-200/35 blur-3xl" />
        </div>
        <div className="container mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2">
          <div>
            <Badge variant="outline" className="rounded-full border-slate-300 bg-white/90 px-4 py-1.5 text-slate-700">
              How It Works
            </Badge>
            <h1 className="mt-5 text-4xl font-extrabold text-slate-950 md:text-6xl">Close high-ticket deals before buyers cool off.</h1>
            <p className="mt-5 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm text-slate-700 md:text-base">
              Quote quickly, share one financing-ready link, and follow up from live buyer signals.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-500">
                <a href={appSignUpUrl}>Start free</a>
              </Button>
              <Button asChild variant="outline" className="rounded-xl border-slate-300">
                <Link to="/pricing">See pricing</Link>
              </Button>
            </div>
          </div>
          <div className="space-y-4">
            <CheckoutMockup />
            <div className="flex flex-wrap items-start gap-4">
              <MobileSellerMockup />
              <AhaToastMockup />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 px-6 py-20 text-slate-100">
        <div className="container mx-auto max-w-6xl">
          <h2 className="max-w-3xl text-3xl font-bold text-white md:text-5xl">From quote to checkout in four steps</h2>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {HOW_IT_WORKS_STEPS.map((step, idx) => (
              <motion.article
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.4, delay: idx * 0.08 }}
                className="rounded-3xl border border-slate-800 bg-slate-900/70 p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="rounded-2xl border border-emerald-300/30 bg-emerald-400/10 p-3 text-emerald-200">
                    <step.icon className="h-5 w-5" />
                  </div>
                  <p className="rounded-full bg-slate-800 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-200">{step.metric}</p>
                </div>
                <h3 className="mt-4 text-2xl font-semibold text-white">{step.title}</h3>
                <p className="mt-2 text-slate-300">{step.detail}</p>
              </motion.article>
            ))}
          </div>
          <div className="mt-8 max-w-sm">
            <BuyerHandoffMockup />
          </div>
          <p className="mt-6 text-sm text-slate-300">
            BNPL availability depends on buyer eligibility, transaction context, and Stripe/payment-method support.
          </p>
        </div>
      </section>

      <section className="bg-[#f8f5ef] px-6 py-20">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Built for real sellers</p>
              <h2 className="mt-3 text-3xl font-bold text-slate-900 md:text-4xl">Use cases for teams selling high-ticket work</h2>
            </div>
            <Button asChild variant="outline" className="rounded-xl border-slate-300 bg-transparent">
              <Link to="/financing">
                Browse all industry pages
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {PERSONAS.map((persona, idx) => (
              <motion.div
                key={persona.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.35, delay: idx * 0.05 }}
              >
                <Card className="h-full rounded-3xl border border-slate-200 bg-slate-50">
                  <CardContent className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{persona.label}</p>
                    <h3 className="mt-2 text-xl font-semibold text-slate-900">{persona.audience}</h3>
                    <p className="mt-3 text-sm text-slate-600">{persona.payoff}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 px-6 py-20">
        <div className="container mx-auto max-w-6xl">
          <h2 className="max-w-3xl text-3xl font-bold text-slate-900 md:text-4xl">Conversion architecture designed for confidence</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            <Card className="rounded-3xl border border-slate-200 bg-white">
              <CardContent className="p-6">
                <div className="inline-flex rounded-2xl bg-indigo-50 p-2 text-indigo-700">
                  <LinkIcon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-xl font-semibold text-slate-900">Share-ready checkout</h3>
                <p className="mt-2 text-sm text-slate-600">Use SMS, link copy, or QR to keep checkout friction low during live conversations.</p>
              </CardContent>
            </Card>
            <Card className="rounded-3xl border border-slate-200 bg-white">
              <CardContent className="p-6">
                <div className="inline-flex rounded-2xl bg-emerald-50 p-2 text-emerald-700">
                  <CreditCard className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-xl font-semibold text-slate-900">Monthly payment visibility</h3>
                <p className="mt-2 text-sm text-slate-600">Buyers evaluate monthly options at decision time, reducing hesitation.</p>
              </CardContent>
            </Card>
            <Card className="rounded-3xl border border-slate-200 bg-white">
              <CardContent className="p-6">
                <div className="inline-flex rounded-2xl bg-slate-100 p-2 text-slate-800">
                  <Sparkles className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-xl font-semibold text-slate-900">Smart follow-up timing</h3>
                <p className="mt-2 text-sm text-slate-600">Reminders show your team when a buyer is active so outreach lands better.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="bg-[#f8f5ef] px-6 py-20">
        <div className="container mx-auto max-w-6xl rounded-[2rem] border border-slate-200 bg-slate-950 p-8 text-white md:p-10">
          <div className="grid items-end gap-8 md:grid-cols-[1.1fr_0.9fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-200">Industry Playbooks</p>
              <h2 className="mt-3 text-3xl font-bold text-white md:text-4xl">Find your business type and launch financing faster</h2>
              <p className="mt-3 max-w-2xl text-slate-200">
                Structured guides for contractors, studios, retail sellers, and service teams.
              </p>
            </div>
            <div className="grid gap-2">
              {PROGRAMMATIC_VERTICALS.slice(0, 5).map((vertical) => (
                <Link
                  key={vertical.slug}
                  to={`/financing/${vertical.slug}`}
                  className="rounded-full border border-slate-700 bg-slate-900/40 px-4 py-2 text-sm text-slate-100 transition hover:border-emerald-300 hover:text-white"
                >
                  {vertical.industry}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 px-6 py-20 text-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-3xl font-bold text-white md:text-5xl">Start with one quote. Close more this week.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-300">If you can quote from a phone, you can run financing-enabled checkout with LendLifted.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="rounded-xl bg-emerald-600 px-6 text-white hover:bg-emerald-500">
              <a href={appSignUpUrl}>
                Start selling
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl border-slate-600 bg-transparent text-slate-100 hover:bg-slate-800">
              <Link to="/contact">Book demo</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;
